import React, { Dispatch, SetStateAction, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import { Check, Delete } from "@material-ui/icons";
import { RootState } from "config/store";
import { format } from "date-fns";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { listItemsInWarehousePageableWithWarehouse } from "shared/network/warehouse.api";
import { deleteProductionItems, Production } from "shared/network/production.api";
import ConfirmationButton from "components/ConfirmationButton";

const WarhouseSelectDialog = ({
  production,
  assemblyItem,
  idx,
  dialogOpen,
  setDialogOpen,
  refetch,
  saveProduction,
}: {
  production: Production;
  assemblyItem: any;
  idx: number;
  dialogOpen: boolean;
  setDialogOpen: Dispatch<SetStateAction<boolean>>;
  refetch: () => void;
  saveProduction: () => void;
}) => {
  const { t } = useTranslation();
  const { tenant } = useSelector((state: RootState) => state.authentication.selectedRelTenant);
  const form = useFormContext();
  const { control } = form;

  const unit =
    assemblyItem?.item?.productUnit === "OWN"
      ? assemblyItem?.item?.customUnitTypeName
      : t(`item.unit.${assemblyItem?.item?.productUnit}`);

  const productionWarehouseQuery = useQuery(
    ["productionWarehouseQuery", tenant?.id, assemblyItem?.item?.id],
    async () => {
      const { data } = await listItemsInWarehousePageableWithWarehouse(
        0,
        2000,
        tenant?.id,
        `item.id=${assemblyItem?.item?.id};date=${format(new Date(), "yyyy-MM-dd")}`,
      );
      return data?.page?.content;
    },
    { enabled: dialogOpen },
  );

  const { fields, append, remove }: any = useFieldArray({
    control,
    name: `relProductionItems.${idx}.warehouses`,
    keyName: "key",
  });

  const [selectedWarehouseId, setSelectedWarehouseId] = useState<string | number>("");

  const selectedIds = fields.map((f: any) => f.warehouse?.id);

  const availableWarehouses =
    productionWarehouseQuery.data?.filter(wh => !selectedIds.includes(wh.warehouseEntry?.id)) || [];

  const [deleteLoading, setDeleteLoading] = useState<number | null>(null);

  return (
    <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>{t("Raktárkészlet kiválasztása")}</DialogTitle>
      <DialogContent style={{ paddingTop: 0 }}>
        <Box fontWeight="bold" fontSize={14} fontFamily="Montserrat" pb={1}>
          {assemblyItem.item?.name}
        </Box>
        <Box display="flex" width="100%">
          <Box flex={1}>
            <span style={{ fontWeight: "bold" }}>{"Receptúra mennyiség: "}</span>
            {assemblyItem.quantity} {unit}
          </Box>
        </Box>
        <Divider style={{ marginTop: 8, marginBottom: 8 }} />
        {productionWarehouseQuery.isLoading && (
          <Box display="flex" justifyContent="center" width="100%">
            <CircularProgress />
          </Box>
        )}
        {!productionWarehouseQuery.isLoading && productionWarehouseQuery.data?.length === 0 ? (
          <Typography>Nincs adat</Typography>
        ) : (
          <>
            <Typography style={{ fontWeight: "bold", marginBottom: 8 }}>
              Kiválasztott raktárak és kivétel mennyisége:
            </Typography>
            <Grid container alignItems="center" spacing={1}>
              {fields.map((field: any, widx: number) => {
                const wQuantity = productionWarehouseQuery.data?.find(
                  wh => wh.warehouseEntry?.id === field.warehouse?.id,
                )?.quantity;

                return (
                  <React.Fragment key={field.key}>
                    <Grid item xs={8}>
                      <Typography>
                        {field.warehouse?.name} ({wQuantity} {unit})
                      </Typography>
                    </Grid>
                    <Grid item xs={4} container justifyContent="flex-end">
                      <Controller
                        control={control}
                        name={`relProductionItems.${idx}.warehouses.${widx}.quantity`}
                        defaultValue={field.quantity}
                        rules={{
                          required: t("validation.required").toString(),
                          min: {
                            value: 1,
                            message: "minimum: 1",
                          },
                          max: {
                            value: wQuantity ?? 0,
                            message: `maximum: ${wQuantity}`,
                          },
                          validate: value =>
                            Number(value) <= (wQuantity ?? 0) || `maximum: ${wQuantity}`,
                        }}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            type="number"
                            size="small"
                            inputProps={{
                              min: 0,
                              max: wQuantity,
                            }}
                            InputLabelProps={{ shrink: true }}
                            style={{ width: 100, marginRight: 8 }}
                            InputProps={{ endAdornment: unit }}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                          />
                        )}
                      />
                      <ConfirmationButton
                        disabled={deleteLoading !== null}
                        size="small"
                        color="primary"
                        buttonType="ICON"
                        title={t("production.deleteTitle")}
                        body={t("production.deleteBody")}
                        onSubmit={async () => {
                          setDeleteLoading(widx);
                          if (field.id) {
                            try {
                              await deleteProductionItems(field.id, tenant.id);
                              remove(widx);
                              refetch();
                            } catch {}
                          } else {
                            remove(widx);
                          }
                          setDeleteLoading(null);
                        }}
                      >
                        {deleteLoading !== null ? <CircularProgress size={20} /> : <Delete />}
                      </ConfirmationButton>
                    </Grid>
                  </React.Fragment>
                );
              })}
            </Grid>
            {!!availableWarehouses?.length && (
              <Box mt={2} display="flex" alignItems="center" gridGap={2}>
                <TextField
                  select
                  label="Raktár"
                  value={selectedWarehouseId}
                  onChange={e => setSelectedWarehouseId(e.target.value)}
                  style={{ minWidth: 180, marginRight: 8 }}
                  size="small"
                >
                  <MenuItem value="">Válassz raktárat</MenuItem>
                  {availableWarehouses.map(wh => (
                    <MenuItem key={wh.warehouseEntry?.id} value={wh.warehouseEntry?.id}>
                      {wh.warehouseEntry?.name} ({wh.quantity} {unit})
                    </MenuItem>
                  ))}
                </TextField>
                <Button
                  variant="outlined"
                  color="primary"
                  disabled={!selectedWarehouseId}
                  onClick={() => {
                    const wh = productionWarehouseQuery.data?.find(
                      w => w.warehouseEntry?.id === selectedWarehouseId,
                    );
                    if (wh) {
                      append({
                        item: assemblyItem?.item,
                        warehouse: wh.warehouseEntry,
                        quantity: "",
                      });
                      setSelectedWarehouseId("");
                    }
                  }}
                >
                  Hozzáad
                </Button>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDialogOpen(false)} color="primary" variant="text">
          {t("common:button.close")}
        </Button>
        <Button
          startIcon={<Check />}
          onClick={async () => {
            const isValid = await form.trigger(`relProductionItems.${idx}.warehouses`);
            if (isValid) {
              saveProduction();
            }
          }}
        >
          Mentés
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WarhouseSelectDialog;
